import { Button } from '@/shared/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/shared/ui/dialog';
import { Input } from '@/shared/ui/input';
import { Label } from '@/shared/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/shared/ui/select';
import { AlertCircle } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useJobTitles, useLineManagers, useUpdateUser } from '../../hooks/useUsers';
import type { UpdateUserBody, UserDetail } from '../../types/user';

import {
  ALLOWED_TEXT_PATTERN,
  PHONE_PATTERN,
  PHONE_VALIDATION_MSG,
  TEXT_VALIDATION_MSG,
} from '@/shared/lib/validation';

interface EditJobInformationDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  userId: string;
  user?: Partial<UserDetail>;
}

interface FormState {
  jobTitle: string;
  discipline: string;
  employmentType: string;
  lineManagerId: string; // kept as string in the Select; parsed to number on submit
  company: string;
  workEmail: string;
  workPhone: string;
  startDate: string;
}

const EMPTY: FormState = {
  jobTitle: '',
  discipline: '',
  employmentType: '',
  lineManagerId: '',
  company: '',
  workEmail: '',
  workPhone: '',
  startDate: '',
};

const toDateInput = (value: string | null | undefined): string =>
  value && value.length >= 10 ? value.slice(0, 10) : '';

export function EditJobInformationDialog({
  open,
  onOpenChange,
  userId,
  user,
}: EditJobInformationDialogProps) {
  const [form, setForm] = useState<FormState>(EMPTY);
  const [errors, setErrors] = useState<Partial<Record<keyof FormState, string>>>({});
  const updateUser = useUpdateUser();
  const { data: jobTitlesData } = useJobTitles();
  const { data: lineManagersData } = useLineManagers();

  const jobTitleOptions = jobTitlesData?.values ?? [];
  // Titles saved while this field was free text (or since removed from Settings)
  // aren't in the lookup, so offer the stored value too — otherwise the Select
  // renders an empty trigger and hides what the user currently has.
  const isUnlistedJobTitle =
    form.jobTitle !== '' &&
    !jobTitleOptions.some((jt: { id: number; name: string }) => jt.name === form.jobTitle);

  useEffect(() => {
    if (open) {
      setErrors({});
      setForm({
        jobTitle: user?.jobTitle ?? '',
        discipline: user?.discipline ?? '',
        employmentType: user?.employmentType ?? '',
        lineManagerId: user?.lineManagerId != null ? String(user.lineManagerId) : '',
        company: user?.company ?? '',
        workEmail: user?.workEmail ?? '',
        workPhone: user?.workPhone ?? '',
        startDate: toDateInput(user?.startDate),
      });
    }
  }, [open, user]);

  const handleChange = (key: keyof FormState, value: string) => {
    setForm((prev) => ({ ...prev, [key]: value }));
    if (errors[key]) setErrors((prev) => ({ ...prev, [key]: '' }));
  };

  const validate = (): boolean => {
    const newErrors: Partial<Record<keyof FormState, string>> = {};

    if (form.discipline && !ALLOWED_TEXT_PATTERN.test(form.discipline)) {
      newErrors.discipline = TEXT_VALIDATION_MSG;
    }
    if (form.company && !ALLOWED_TEXT_PATTERN.test(form.company)) {
      newErrors.company = TEXT_VALIDATION_MSG;
    }
    if (form.workPhone && !PHONE_PATTERN.test(form.workPhone)) {
      newErrors.workPhone = PHONE_VALIDATION_MSG;
    }
    if (form.startDate) {
      const today = new Date().toISOString().split('T')[0];
      if (form.startDate > today) {
        newErrors.startDate = 'Start date cannot be in the future';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validate()) return;
    const data: UpdateUserBody = {};
    if (form.jobTitle) data.jobTitle = form.jobTitle;
    if (form.discipline) data.discipline = form.discipline;
    if (form.employmentType) data.employmentType = form.employmentType;
    if (form.company) data.company = form.company;
    if (form.workEmail) data.workEmail = form.workEmail;
    if (form.workPhone) data.workPhone = form.workPhone;
    if (form.startDate) data.startDate = form.startDate;
    if (form.lineManagerId) {
      const parsed = Number(form.lineManagerId);
      if (Number.isFinite(parsed) && parsed > 0) {
        data.lineManagerId = parsed;
      }
    }
    await updateUser.mutateAsync({ userId, data });
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Edit Job Information</DialogTitle>
          <DialogDescription>Update employment and role details.</DialogDescription>
        </DialogHeader>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 py-2">
          <div className="space-y-2">
            <Label htmlFor="jobTitle">Job Title</Label>
            <Select value={form.jobTitle} onValueChange={(v) => handleChange('jobTitle', v)}>
              <SelectTrigger id="jobTitle">
                <SelectValue placeholder="Select job title" />
              </SelectTrigger>
              <SelectContent>
                {isUnlistedJobTitle && (
                  <SelectItem value={form.jobTitle}>{form.jobTitle}</SelectItem>
                )}
                {jobTitleOptions.map((jt: { id: number; name: string }) => (
                  <SelectItem key={jt.id} value={jt.name}>
                    {jt.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-2">
            <Label htmlFor="discipline">Discipline</Label>
            <Input
              id="discipline"
              value={form.discipline}
              onChange={(e) => handleChange('discipline', e.target.value)}
              placeholder="e.g. Gas, Water, Electric"
              className={errors.discipline ? 'border-red-500 focus-visible:ring-red-500' : ''}
            />
            {errors.discipline && (
              <p className="text-xs text-red-500 flex items-center gap-1">
                <AlertCircle className="w-3 h-3" />
                {errors.discipline}
              </p>
            )}
          </div>
          <div className="space-y-2">
            <Label htmlFor="employmentType">Employment Type</Label>
            <Select
              value={form.employmentType}
              onValueChange={(v) => handleChange('employmentType', v)}
            >
              <SelectTrigger id="employmentType">
                <SelectValue placeholder="Select type" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="PAYE">PAYE</SelectItem>
                <SelectItem value="Direct Employee">Direct Employee</SelectItem>
                <SelectItem value="Contractor">Contractor</SelectItem>
                <SelectItem value="Subcontractor">Subcontractor</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-2">
            <Label htmlFor="lineManagerId">Line Manager</Label>
            <Select
              value={form.lineManagerId}
              onValueChange={(v) => handleChange('lineManagerId', v)}
            >
              <SelectTrigger id="lineManagerId">
                <SelectValue placeholder="Select line manager" />
              </SelectTrigger>
              <SelectContent>
                {(lineManagersData?.values ?? []).map((lm: { id: number; name: string }) => (
                  <SelectItem key={lm.id} value={String(lm.id)}>
                    {lm.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-2">
            <Label htmlFor="company">Company</Label>
            <Input
              id="company"
              value={form.company}
              onChange={(e) => handleChange('company', e.target.value)}
              className={errors.company ? 'border-red-500 focus-visible:ring-red-500' : ''}
            />
            {errors.company && (
              <p className="text-xs text-red-500 flex items-center gap-1">
                <AlertCircle className="w-3 h-3" />
                {errors.company}
              </p>
            )}
          </div>
          <div className="space-y-2">
            <Label htmlFor="workEmail">Work Email</Label>
            <Input
              id="workEmail"
              type="email"
              value={form.workEmail}
              onChange={(e) => handleChange('workEmail', e.target.value)}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="workPhone">Work Phone</Label>
            <Input
              id="workPhone"
              value={form.workPhone}
              onChange={(e) => handleChange('workPhone', e.target.value)}
              className={errors.workPhone ? 'border-red-500 focus-visible:ring-red-500' : ''}
            />
            {errors.workPhone && (
              <p className="text-xs text-red-500 flex items-center gap-1">
                <AlertCircle className="w-3 h-3" />
                {errors.workPhone}
              </p>
            )}
          </div>
          <div className="space-y-2">
            <Label htmlFor="startDate">Start Date</Label>
            <Input
              id="startDate"
              type="date"
              value={form.startDate}
              max={new Date().toISOString().split('T')[0]}
              onChange={(e) => handleChange('startDate', e.target.value)}
              className={errors.startDate ? 'border-red-500 focus-visible:ring-red-500' : ''}
            />
            {errors.startDate && (
              <p className="text-xs text-red-500 flex items-center gap-1">
                <AlertCircle className="w-3 h-3" />
                {errors.startDate}
              </p>
            )}
          </div>
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={updateUser.isPending}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={updateUser.isPending}
            className="bg-orange-600 hover:bg-orange-700 text-white"
          >
            {updateUser.isPending ? 'Saving…' : 'Save Changes'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
